import React, { useCallback, useContext, useEffect, useState } from "react";
import MultiSelectWithDependentCities from '../MultiSelects/MultiSelectWithDependentCities';
import MultiSelectWithDependentZipCodes from '../MultiSelects/MultiSelectWithDependentZipCodes';
import MultiSelectInColumnWithLabel from '../MultiSelects/MultiSelectInColumnWithLabel';
import MultiSelectFoundationYear from '../MultiSelects/MultiSelectFoundationYear';
import MultiSelectPlatformsAndContactMethods from '../MultiSelects/MultiSelectPlatformsAndContactMethods';
import MultiSelectWithDependentServiceSubtypes from '../MultiSelects/MultiSelectWithDependentServiceSubtypes';
import { SearchContext } from '../../context/SearchContext';
import api from '../../api';
import fetchCityByIP from '../FetchCityByIP';
import './pageLawAndVendorsList.css';
import GeolocationButton from '../GeolocationButton';
import SearchedVendors from '../Vendors/SearchedVendors';

const PageVendors = ({ onShowPopup, onSearch }) => {
    // ШАГ 1: Все хуки вызываются безусловно в самом верху.
    const context = useContext(SearchContext);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [specializations, setSpecializations] = useState([]);
    const [selectedZipCodes, setSelectedZipCodes] = useState([]);
    const [selectedState, setSelectedState] = useState([]);
    const [selectedCity, setSelectedCity] = useState([]);
    const [selectedServiceSubtype, setSelectedServiceSubtype] = useState([]);
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
    const [selectedFoundationYears, setSelectedFoundationYears] = useState([]);
    const [selectedContactMethods, setSelectedContactMethods] = useState([]);
    const [selectedSocialMedia, setSelectedSocialMedia] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState([]);
    const [userLocation, setUserLocation] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [isConsultationCheck, setIsConsultationCheck] = useState(false);
    const [isAwardsCheck, setIsAwardsCheck] = useState(false);
    const [isZipcodeProvided, setIsZipcodeProvided] = useState(false);
    const [prevRequestData, setPrevRequestData] = useState(null);

    // Используем защитное присваивание с || {}, чтобы избежать ошибок при деструктуризации
    const {
        setVendorResults,
        changeSearchVendorSended,
        setVendorPage,
        setSpecialization,
        setCities,
        setStates,
        setZipCode,
        setFreeConsultation,
        setFoundationYears,
        setVendorFilters,
        setLoading,
        zipCode,
    } = context || {}; // Защита от null/undefined

    useEffect(() => {
        // Проверяем, что context существует, перед тем как выполнять логику
        if (!context) return;

        const fetchSpecializations = async () => {
            try {
                const response = await api.get('/specializations-full/');
                setSpecializations(response.data.map(spec => ({ name: spec.name, code: spec.name })));
            } catch (error) {
                console.error('Error fetching specializations:', error);
            }
        };

        const fetchLanguages = async () => {
            try {
                const response = await api.get('/languages/');
                const uniqueLanguages = Array.from(new Set(response.data.map(lang => lang.name)))
                    .map(name => response.data.find(lang => lang.name === name));
                setLanguages(uniqueLanguages);
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };

        fetchSpecializations();
        fetchLanguages();
    }, [context]);

    useEffect(() => {
        if (!context) return;

        const getCityFromIP = async () => {
            const city = await fetchCityByIP();
            if (city) {
                setSelectedCity([city]);
            }
        };

        if (!userLocation && !zipCode) {
            getCityFromIP();
        }
    }, [userLocation, zipCode, context]);

    const handleSearch = async (page = 1) => {
    let requestData = {};
    const isEmptyForm =
        !isZipcodeProvided &&
        (!selectedCity || selectedCity.length === 0) &&
        (!selectedState || selectedState.length === 0) &&
        (!selectedZipCodes || selectedZipCodes.length === 0) &&
        (!selectedServiceSubtype || selectedServiceSubtype.length === 0) &&
        (!selectedServiceTypes || selectedServiceTypes.length === 0) &&
        (!selectedFoundationYears || selectedFoundationYears.length === 0) &&
        (!selectedContactMethods || selectedContactMethods.length === 0) &&
        (!selectedSocialMedia || selectedSocialMedia.length === 0) &&
        (!selectedLanguage || selectedLanguage.length === 0) &&
        !isConsultationCheck &&
        !isAwardsCheck;

    if (isEmptyForm) {
        return;
    }

    if (isZipcodeProvided) {
        requestData = {
            zip_codes: [{code: {name: zipCode, code: zipCode}}],
            service_types: Array.isArray(selectedServiceTypes) ? selectedServiceTypes.map(type => ({ id: type.service_type_id, name: type.service_type_name })) : [],
            service_subtypes: Array.isArray(selectedServiceSubtype) ? selectedServiceSubtype.map(subtype => subtype.name) : [],
            foundation_years: selectedFoundationYears.map(year => ({year: year})),
            contact_methods: selectedContactMethods.map(method => ({name: method})),
            social_media: Array.isArray(selectedSocialMedia) ? selectedSocialMedia.map(media => ({platform: media})) : [],
            languages: Array.isArray(selectedLanguage) ? selectedLanguage.map(lang => ({name: lang})) : [],
            workers: isConsultationCheck,
            has_awards: isAwardsCheck,
            page
        };
    } else {
        requestData = {
            cities: selectedCity.map(city => ({id: city.city_id, name: city.name})),
            service_types: Array.isArray(selectedServiceTypes) ? selectedServiceTypes.map(type => ({ id: type.service_type_id, name: type.service_type_name })) : [],
            service_subtypes: Array.isArray(selectedServiceSubtype) ? selectedServiceSubtype.map(subtype => subtype.name) : [],
            states: selectedState.map(state => ({state_id: state.state_id, name: state.name})),
            zip_codes: selectedZipCodes.map(zip => ({code: zip})),
            foundation_years: selectedFoundationYears.map(year => ({year: year})),
            contact_methods: selectedContactMethods.map(method => ({name: method})),
            social_media: Array.isArray(selectedSocialMedia) ? selectedSocialMedia.map(media => ({platform: media})) : [],
            languages: Array.isArray(selectedLanguage) ? selectedLanguage.map(lang => ({name: lang})) : [],
            workers: isConsultationCheck,
            has_awards: isAwardsCheck,
            page
        };
    }

    if (JSON.stringify(requestData) === JSON.stringify(prevRequestData)) {
        return;
    }

    setLoading(true);
    try {
        changeSearchVendorSended();
        const response = await api.post('/vendors-searching/', requestData);
        const aiSuggestion = response.data.suggestion || "AI suggests you contact a vendor specializing";

        const normalizedFilters = {...requestData, page: 1};
        if (setVendorFilters) {
            setVendorFilters(normalizedFilters);
        }

        if (setVendorPage) setVendorPage(page);
        if (setSpecialization) setSpecialization(selectedServiceSubtype);
        if (setCities) setCities(selectedCity);
        if (setStates) setStates(selectedState);

        const zipCodesForContext = isZipcodeProvided ? [zipCode] : selectedZipCodes;
        if (setZipCode) setZipCode(zipCodesForContext);
        if (setFreeConsultation) setFreeConsultation(isConsultationCheck);
        if (setFoundationYears) setFoundationYears(selectedFoundationYears);

        if (onSearch) {
            onSearch({results: response.data.results, count: response.data.count, page, suggestion: aiSuggestion});
        }

        setVendorResults({
            results: response.data.results,
            count: response.data.count,
            page,
            suggestion: aiSuggestion
        });
        setPrevRequestData(requestData);
    } catch (error) {
        console.error('Ошибка при получении результатов:', error);
        const aiSuggestion = (error.response && error.response.data && error.response.data.suggestion) || "AI suggests you contact a vendor specializing";
        if (onSearch) {
            onSearch({results: [], count: 0, page: 1, suggestion: aiSuggestion});
        }
    } finally {
        setLoading(false);
    }
};

    const clearLocation = () => {
        setUserLocation(null);
        if (setZipCode) setZipCode('');
        setIsZipcodeProvided(false);
    };

    const handleLocationObtained = (zip, location, isEnabled) => {
        if (isEnabled) {
            setUserLocation(location);
            if (setZipCode) setZipCode(zip);
            if (zip) {
                setIsZipcodeProvided(true);
            }
        } else {
            clearLocation();
        }
    };

    const handleServiceSubtypeChange = ({selectedServiceSubtypes, selectedServiceTypes}) => {
        setSelectedServiceSubtype(selectedServiceSubtypes);
        setSelectedServiceTypes(selectedServiceTypes);
    };

    const handleConsultationClick = useCallback(() => setIsConsultationCheck(prev => !prev), []);
    const handleAwardsClick = useCallback(() => setIsAwardsCheck(prev => !prev), []);
    const onCityStateChange = (cities, states) => {
        setSelectedCity(cities);
        setSelectedState(states);
    };
    const onZipCodeChange = (zipCodes) => setSelectedZipCodes(zipCodes);
    const onContactMethodsChange = (contactMethods) => setSelectedContactMethods(contactMethods);
    const onSocialMediaChange = (socialMedia) => setSelectedSocialMedia(socialMedia);
    const onLanguageChange = (languages) => setSelectedLanguage(languages);

    // ШАГ 2: Условный рендеринг выполняется ПОСЛЕ всех хуков.
    if (!context) {
        return <div>Loading...</div>;
    }

    // ШАГ 3: Возвращаем JSX
    return (
        <div className='page-list'>
            <div className='header__search'>
                <div className='container'>
                    <div className='search__advanced has-button show'>
                        <div className='grid-container'>
                            <MultiSelectWithDependentCities
                                label="City"
                                onChange={onCityStateChange}
                                states={selectedState}
                                placeholder="Select city"
                                isZipcodeProvided={isZipcodeProvided}
                                disabled={isZipcodeProvided}
                            />
                            <MultiSelectWithDependentZipCodes
                                label="ZIP Codes"
                                onChange={onZipCodeChange}
                                cities={selectedCity}
                                placeholder="Select zip codes"
                                isZipcodeProvided={isZipcodeProvided}
                                disabled={isZipcodeProvided}
                            />
                            <MultiSelectWithDependentServiceSubtypes
                                label="Service Subtypes"
                                onChange={handleServiceSubtypeChange}
                                serviceType={selectedServiceSubtype}
                                placeholder="Select service subtypes"
                            />
                            <MultiSelectInColumnWithLabel
                                label="Languages"
                                options={languages.map(lang => ({name: lang.name, code: lang.name}))}
                                onChange={onLanguageChange}
                                placeholder="Select languages"
                            />
                            <MultiSelectFoundationYear
                                label="Foundation Year"
                                onChange={setSelectedFoundationYears}
                            />
                            <MultiSelectPlatformsAndContactMethods
                                label="Contact Methods"
                                onChange={(selectedOptions) => {
                                    setSelectedContactMethods(selectedOptions.contactMethods);
                                    setSelectedSocialMedia(selectedOptions.socialMedia);
                                }}
                            />
                        </div>
                        <div className='header__search__buttons'>
                            <div className='column desktop'>
                                <label className="label">Workers</label>
                                <label onClick={handleConsultationClick}
                                       className={isConsultationCheck ? 'consultations-checkbox checked' : 'consultations-checkbox'}>
                                    <span>All</span>
                                    <span>Yes</span>
                                </label>
                            </div>
                            <div className='column mobile'>
                                <label className="label">&nbsp;</label>
                                <div className='search__radius'>
                                    <span>Workers</span>
                                    <label
                                        className={`toggle ${isConsultationCheck ? 'checked' : ''}`}
                                        onClick={handleConsultationClick}
                                    >
                                        <div className='toggle__container'>
                                            <div className='toggle__circle'></div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            <div className='column column-button'>
                                <label className="label">&nbsp;</label>
                                <label>
                                    <button className='button button-black' onClick={() => handleSearch(1)}>Find
                                        Vendor
                                    </button>
                                </label>
                            </div>
                            <div className='column desktop'>
                                <label className="label">Awards</label>
                                <label onClick={handleAwardsClick}
                                       className={isAwardsCheck ? 'consultations-checkbox checked' : 'consultations-checkbox'}>
                                    <span>All</span>
                                    <span>Yes</span>
                                </label>
                            </div>
                            <div className='column mobile'>
                                <label className="label">&nbsp;</label>
                                <div className='search__radius'>
                                    <span>Awards</span>
                                    <label
                                        className={`toggle ${isAwardsCheck ? 'checked' : ''}`}
                                        onClick={handleAwardsClick}
                                    >
                                        <div className='toggle__container'>
                                            <div className='toggle__circle'></div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            <div className='column'>
                                <label className="label">&nbsp;</label>
                                <GeolocationButton onLocationObtained={handleLocationObtained}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SearchedVendors onShowPopup={onShowPopup} onShowMore={handleSearch}/>
        </div>
    );
};

export default PageVendors;
